import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

class ProductVariantSpecTable extends Component {
  /** @type {Map<string, Record<string, string | null>>} */
  #variantSpecs = new Map();

  /** @type {HTMLTableElement | null} */
  #table = null;

  /** @type {HTMLTableSectionElement | null} */
  #tbody = null;

  /** @type {HTMLInputElement | null} */
  #variantIdInput = null;

  /** @type {MutationObserver | null} */
  #variantInputObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#table = this.querySelector('.product-variant-spec-table__table');
    this.#tbody = this.querySelector('.product-variant-spec-table__tbody');
    this.#hydrateVariantData();
    this.#bindVariantInput();

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateContent);
    document.addEventListener(ThemeEvents.variantUpdate, this.updateContent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateContent);
    document.removeEventListener(ThemeEvents.variantUpdate, this.updateContent);
    this.#variantInputObserver?.disconnect();
    this.#variantInputObserver = null;
  }

  /**
   * Updates the variant specs table on variant changes.
   * @param {VariantUpdateEvent} event
   */
  updateContent = (event) => {
    const variantId = event.detail?.resource?.id;
    if (variantId !== undefined && variantId !== null) {
      this.#renderRows(String(variantId));
      return;
    }

    // Fallback for cases where variant id is unavailable in event payload.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const selector = `product-variant-spec-table[data-block-id="${this.dataset.blockId}"]`;
    let newContent = event.detail.data.html.querySelector(selector);
    if (!newContent) {
      newContent = event.detail.data.html.querySelector('product-variant-spec-table');
    }
    if (!newContent) return;

    morph(this, newContent, { childrenOnly: true });
    this.#table = this.querySelector('.product-variant-spec-table__table');
    this.#tbody = this.querySelector('.product-variant-spec-table__tbody');
    this.#hydrateVariantData();
    this.#bindVariantInput();
  };

  #hydrateVariantData() {
    this.#variantSpecs.clear();
    const dataScript = this.querySelector('.product-variant-spec-table__data');
    if (!(dataScript instanceof HTMLScriptElement)) return;

    let parsed;
    try {
      parsed = JSON.parse(dataScript.textContent || '{}');
    } catch {
      return;
    }

    const variants = Array.isArray(parsed.variants) ? parsed.variants : [];
    for (const variant of variants) {
      if (!variant || variant.id === undefined || variant.id === null) continue;
      this.#variantSpecs.set(String(variant.id), variant);
    }

    const initialVariantId = parsed.initialVariantId;
    if (initialVariantId !== undefined && initialVariantId !== null) {
      this.#renderRows(String(initialVariantId));
    }
  }

  #bindVariantInput() {
    this.#variantInputObserver?.disconnect();
    this.#variantInputObserver = null;

    const scope = this.closest('.shopify-section, dialog') || document;
    const variantIdInput = scope.querySelector('form[action*="/cart"] input[name="id"], input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;

    this.#variantIdInput = variantIdInput;
    if (variantIdInput.value) {
      this.#renderRows(String(variantIdInput.value));
    }

    this.#variantInputObserver = new MutationObserver(() => {
      const nextId = this.#variantIdInput?.value;
      if (nextId) this.#renderRows(String(nextId));
    });

    this.#variantInputObserver.observe(variantIdInput, {
      attributes: true,
      attributeFilter: ['value'],
    });
  }

  /**
   * @param {string} variantId
   */
  #renderRows(variantId) {
    if (!this.#table || !this.#tbody) return;

    const variant = this.#variantSpecs.get(variantId);
    const rows = [
      ['Length', variant?.length],
      ['Width', variant?.width],
      ['Height', variant?.height],
      ['Diameter', variant?.diameter],
      ['Weight', variant?.weight],
    ].filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '');

    if (rows.length === 0) {
      this.#tbody.innerHTML = '';
      this.#table.hidden = true;
      return;
    }

    this.#table.hidden = false;
    this.#tbody.innerHTML = rows
      .map(
        ([label, value]) =>
          `<tr><th scope="row">${this.#escapeHtml(String(label))}</th><td>${this.#escapeHtml(String(value))}</td></tr>`
      )
      .join('');
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('product-variant-spec-table')) {
  customElements.define('product-variant-spec-table', ProductVariantSpecTable);
}
